import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Header, Table } from 'semantic-ui-react'
import styled from 'styled-components'
import { CoolFarmCropResultsType } from '~/api/services/coolFarmCrop'
import { formatNumber } from '~/utils/helpers'

interface DetailedDataTableProps {
  results: CoolFarmCropResultsType
}

const Segment = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`

const HideDataLink = styled.a`
  color: #007bff;
  cursor: pointer;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`

const safeParseFloat = (value: string | number | undefined): number => {
  if (value === undefined || value === null) return 0
  return parseFloat(String(value))
}

const formatChartNumber = (num: number) => {
  if (num >= 1000000) {
    return formatNumber(num / 1000000, 2) + 'M'
  } else if (num >= 1000) {
    return formatNumber(num / 1000, 2) + 'k'
  }
  return formatNumber(num, 2)
}

const DetailedDataTable: React.FC<DetailedDataTableProps> = ({ results }) => {
  const { t } = useTranslation()
  const [showDetailedData, setShowDetailedData] = useState(true)

  const chartData = useMemo(() => {
    return [
      {
        name: t('Seed production'),
        value: 0,
        co2: 0,
        n2o: 0,
        ch4: 0,
        total: 0,
        perHa: 0,
        perKg: 0
      },
      {
        name: t('Residue management'),
        value: safeParseFloat(results.residueTotalEmission),
        co2: safeParseFloat(results.residueCo2Emission),
        n2o: safeParseFloat(results.residueN2oEmission),
        ch4: safeParseFloat(results.residueCh4Emission),
        total: safeParseFloat(results.residueTotalEmission),
        perHa: safeParseFloat(results.residueTotalEmissionPerArea),
        perKg: safeParseFloat(results.residueTotalEmissionPerProduct)
      },
      {
        name: t('Fertiliser production*'),
        value: safeParseFloat(results.fertiliserProductionTotalEmission),
        co2: safeParseFloat(results.fertiliserProductionCo2Emission),
        n2o: safeParseFloat(results.fertiliserProductionN2oEmission),
        ch4: safeParseFloat(results.fertiliserProductionCh4Emission),
        total: safeParseFloat(results.fertiliserProductionTotalEmission),
        perHa: safeParseFloat(results.fertiliserProductionTotalEmissionPerArea),
        perKg: safeParseFloat(results.fertiliserProductionTotalEmissionPerProduct)
      },
      {
        name: t('Soil / fertiliser'),
        value: safeParseFloat(results.fertiliserSoilTotalEmission),
        co2: safeParseFloat(results.fertiliserSoilCo2Emission),
        n2o: safeParseFloat(results.fertiliserSoilN2oEmission),
        ch4: safeParseFloat(results.fertiliserSoilCh4Emission),
        total: safeParseFloat(results.fertiliserSoilTotalEmission),
        perHa: safeParseFloat(results.fertiliserSoilTotalEmissionPerArea),
        perKg: safeParseFloat(results.fertiliserSoilTotalEmissionPerProduct)
      },
      {
        name: t('Paddy methane'),
        value: 0,
        co2: 0,
        n2o: 0,
        ch4: 0,
        total: 0,
        perHa: 0,
        perKg: 0
      },
      {
        name: t('Crop protection'),
        value: safeParseFloat(results.pesticideTotalEmission),
        co2: safeParseFloat(results.pesticideCo2Emission),
        n2o: safeParseFloat(results.pesticideN2oEmission),
        ch4: safeParseFloat(results.pesticideCh4Emission),
        total: safeParseFloat(results.pesticideTotalEmission),
        perHa: safeParseFloat(results.pesticideTotalEmissionPerArea),
        perKg: safeParseFloat(results.pesticideTotalEmissionPerProduct)
      },
      {
        name: t('Carbon stock changes'),
        value: safeParseFloat(results.managementChangesTotalEmission),
        co2: safeParseFloat(results.managementChangesCo2Emission),
        n2o: safeParseFloat(results.managementChangesN2oEmission),
        ch4: safeParseFloat(results.managementChangesCh4Emission),
        total: safeParseFloat(results.managementChangesTotalEmission),
        perHa: safeParseFloat(results.managementChangesTotalEmissionPerArea),
        perKg: safeParseFloat(results.managementChangesTotalEmissionPerProduct)
      },
      {
        name: t('Energy use (field)'),
        value: safeParseFloat(results.fieldEnergyTotalEmission),
        co2: safeParseFloat(results.fieldEnergyCo2Emission),
        n2o: safeParseFloat(results.fieldEnergyN2oEmission),
        ch4: safeParseFloat(results.fieldEnergyCh4Emission),
        total: safeParseFloat(results.fieldEnergyTotalEmission),
        perHa: safeParseFloat(results.fieldEnergyTotalEmissionPerArea),
        perKg: safeParseFloat(results.fieldEnergyTotalEmissionPerProduct)
      },
      {
        name: t('Energy use (processing)'),
        value: safeParseFloat(results.machineryTotalEmission),
        co2: safeParseFloat(results.machineryCo2Emission),
        n2o: safeParseFloat(results.machineryN2oEmission),
        ch4: safeParseFloat(results.machineryCh4Emission),
        total: safeParseFloat(results.machineryTotalEmission),
        perHa: safeParseFloat(results.machineryTotalEmissionPerArea),
        perKg: safeParseFloat(results.machineryTotalEmissionPerProduct)
      },
      {
        name: t('Waste water'),
        value: safeParseFloat(results.wasteWaterTotalEmission),
        co2: safeParseFloat(results.wasteWaterCo2Emission),
        n2o: safeParseFloat(results.wasteWaterN2oEmission),
        ch4: safeParseFloat(results.wasteWaterCh4Emission),
        total: safeParseFloat(results.wasteWaterTotalEmission),
        perHa: safeParseFloat(results.wasteWaterTotalEmissionPerArea),
        perKg: safeParseFloat(results.wasteWaterTotalEmissionPerProduct)
      },
      {
        name: t('Off-farm transport'),
        value: safeParseFloat(results.transportationTotalEmission),
        co2: safeParseFloat(results.transportationCo2Emission),
        n2o: safeParseFloat(results.transportationN2oEmission),
        ch4: safeParseFloat(results.transportationCh4Emission),
        total: safeParseFloat(results.transportationTotalEmission),
        perHa: safeParseFloat(results.transportationTotalEmissionPerArea),
        perKg: safeParseFloat(results.transportationTotalEmissionPerProduct)
      }
    ]
  }, [results, t])

  return (
    <Segment>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Header as='h3'>
          {t('Detailed data (all values in kg)')}
        </Header>
        <HideDataLink onClick={() => setShowDetailedData(!showDetailedData)}>
          {showDetailedData ? t('Hide data') : t('Show data')}
        </HideDataLink>
      </div>
      
      {showDetailedData && (
        <Table celled style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={{ background: '#f8f9fa', fontWeight: '600' }}>
                {t('Sources')}
              </Table.HeaderCell>
              <Table.HeaderCell style={{ background: '#f8f9fa', fontWeight: '600' }}>
                <div className='unit' dangerouslySetInnerHTML={{ __html: '<span>kg CO<sub>2</sub></span>' }}/>
              </Table.HeaderCell>
              <Table.HeaderCell style={{ background: '#f8f9fa', fontWeight: '600' }}>
                <div className='unit' dangerouslySetInnerHTML={{ __html: '<span>kg N<sub>2</sub>O</span>' }}/>
              </Table.HeaderCell>
              <Table.HeaderCell style={{ background: '#f8f9fa', fontWeight: '600' }}>
                <div className='unit' dangerouslySetInnerHTML={{ __html: '<span>kg CH<sub>4</sub></span>' }}/>
              </Table.HeaderCell>
              <Table.HeaderCell style={{ background: '#f8f9fa', fontWeight: '600' }}>
                <div className='unit' dangerouslySetInnerHTML={{ __html: '<span>kg Total CO<sub>2</sub>eq</span>' }}/>
              </Table.HeaderCell>
              <Table.HeaderCell style={{ background: '#f8f9fa', fontWeight: '600' }}>
                {t('Per ha')}
              </Table.HeaderCell>
              <Table.HeaderCell style={{ background: '#f8f9fa', fontWeight: '600' }}>
                {t('Per kg')}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {chartData.map((item, index) => (
              <Table.Row key={index}>
                <Table.Cell style={{ borderTop: '1px solid #e9ecef' }}>
                  {item.name}
                </Table.Cell>
                <Table.Cell style={{ borderTop: '1px solid #e9ecef' }}>
                  {formatChartNumber(item.co2)}
                </Table.Cell>
                <Table.Cell style={{ borderTop: '1px solid #e9ecef' }}>
                  {formatChartNumber(item.n2o)}
                </Table.Cell>
                <Table.Cell style={{ borderTop: '1px solid #e9ecef' }}>
                  {formatChartNumber(item.ch4)}
                </Table.Cell>
                <Table.Cell style={{ borderTop: '1px solid #e9ecef' }}>
                  {formatChartNumber(item.total)}
                </Table.Cell>
                <Table.Cell style={{ borderTop: '1px solid #e9ecef' }}>
                  {formatChartNumber(item.perHa)}
                </Table.Cell>
                <Table.Cell style={{ borderTop: '1px solid #e9ecef' }}>
                  {formatChartNumber(item.perKg)}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
      
      <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666' }}>
        * {t('Calculated with validated default values for fertiliser production.')}
      </div>
    </Segment>
  )
}

export default DetailedDataTable
